/**
 * Admin page for managing `core.verification_whitelist` — email-keyed
 * IDV bypass list. See backend/src/routes/admin-verification-whitelist.ts
 * for endpoint contracts + docs/runbooks/verification-whitelist.md for
 * operator guidance.
 *
 * Behaviours:
 *   - Lists active entries by default; toggle to show revoked too.
 *   - Add: POST { email, reason, expires_at?, notes? }. Backend
 *     retroactively flips blue_check on the matching users_core row
 *     if one exists; success message reports whether that happened.
 *   - Revoke: soft-deletes (is_active=FALSE). Does NOT auto-revoke
 *     blue_check on the existing user — that's a separate manual step
 *     intentionally surfaced as a banner after revoke.
 */
import { useCallback, useEffect, useState } from 'react';
import { useApiUrl } from '@refinedev/core';
import { ShieldCheck, Plus, X, Search, Loader2, AlertCircle, Check } from 'lucide-react';
import { formatDate } from '@/lib/utils';
import { getIdToken } from '@/providers/auth-provider';

interface WhitelistEntry {
  email_lower: string;
  reason: string;
  is_active: boolean;
  expires_at: string | null;
  created_at: string;
  created_by: string | null;
  revoked_at: string | null;
  revoked_by: string | null;
  notes: string | null;
}

interface ListResponse {
  entries: WhitelistEntry[];
  total: number;
}

export default function VerificationWhitelistPage() {
  const apiUrl = useApiUrl();
  const [entries, setEntries] = useState<WhitelistEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [includeRevoked, setIncludeRevoked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [lastRevokeNote, setLastRevokeNote] = useState<string | null>(null);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getIdToken();
      const params = new URLSearchParams();
      if (search.trim()) params.set('search', search.trim());
      if (includeRevoked) params.set('include_revoked', 'true');
      const res = await fetch(
        `${apiUrl}/v1/admin/verification-whitelist?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
      const data = (await res.json()) as ListResponse;
      setEntries(data.entries);
      setTotal(data.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load whitelist');
    } finally {
      setLoading(false);
    }
  }, [apiUrl, search, includeRevoked]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const handleRevoke = useCallback(
    async (entry: WhitelistEntry) => {
      if (!window.confirm(`Revoke whitelist for "${entry.email_lower}"?\n\nThe user's existing blue_check stays in place — revoke that separately if needed.`)) {
        return;
      }
      try {
        const token = await getIdToken();
        const res = await fetch(
          `${apiUrl}/v1/admin/verification-whitelist/${encodeURIComponent(entry.email_lower)}`,
          { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } },
        );
        if (!res.ok) throw new Error(`Revoke failed: ${res.status}`);
        setLastRevokeNote(
          `Revoked "${entry.email_lower}". If this user is currently verified-via-whitelist, manually clear their verification_trust row to remove blue_check.`,
        );
        await fetchEntries();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Revoke failed');
      }
    },
    [apiUrl, fetchEntries],
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2 text-foreground">
            <ShieldCheck className="w-6 h-6" />
            Verification Whitelist
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Emails on this list bypass ID verification on signup. Persists across account deletes.
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700"
        >
          <Plus className="w-4 h-4" /> Add email
        </button>
      </div>

      {lastRevokeNote && (
        <div className="flex items-start gap-2 p-3 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-300 text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div className="flex-1">{lastRevokeNote}</div>
          <button onClick={() => setLastRevokeNote(null)} className="p-1 -m-1">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-3 py-2 w-full text-sm border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-foreground">
          <input
            type="checkbox"
            checked={includeRevoked}
            onChange={(e) => setIncludeRevoked(e.target.checked)}
            className="w-4 h-4 rounded"
          />
          Show revoked
        </label>
        <span className="text-sm text-muted-foreground ml-auto">
          {total} {total === 1 ? 'entry' : 'entries'}
        </span>
      </div>

      {error && (
        <div className="p-3 rounded-md bg-destructive/10 border border-destructive/30 text-destructive text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : entries.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-sm">
          No whitelist entries yet. Click <b>Add email</b> to create the first one.
        </div>
      ) : (
        <div className="border rounded-md overflow-hidden bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/30 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">Reason</th>
                <th className="text-left px-4 py-3">Added</th>
                <th className="text-left px-4 py-3">Expires</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.email_lower} className="border-t hover:bg-muted/30">
                  <td className="px-4 py-3 font-mono text-xs text-foreground">{entry.email_lower}</td>
                  <td className="px-4 py-3 text-foreground">{entry.reason}</td>
                  <td className="px-4 py-3 text-muted-foreground">{formatDate(entry.created_at)}</td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {entry.expires_at ? formatDate(entry.expires_at) : '—'}
                  </td>
                  <td className="px-4 py-3">
                    {entry.is_active ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-500/15 text-green-700 dark:text-green-300">
                        <Check className="w-3 h-3" /> Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-muted text-muted-foreground">
                        Revoked
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {entry.is_active && (
                      <button
                        onClick={() => handleRevoke(entry)}
                        className="text-destructive hover:underline text-xs font-medium"
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showAddModal && (
        <AddWhitelistModal
          apiUrl={apiUrl}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            fetchEntries();
          }}
        />
      )}
    </div>
  );
}

interface AddModalProps {
  apiUrl: string;
  onClose: () => void;
  onSuccess: () => void;
}

function AddWhitelistModal({ apiUrl, onClose, onSuccess }: AddModalProps) {
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setSubmitting(true);
      setError(null);
      try {
        const token = await getIdToken();
        const res = await fetch(`${apiUrl}/v1/admin/verification-whitelist`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: email.trim(),
            reason: reason.trim(),
            expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
            notes: notes.trim() || null,
          }),
        });
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data?.error || `Add failed: ${res.status}`);
        }
        const retroNote = data.retroactively_applied_to_user_id
          ? ` Retroactively verified existing user ${data.retroactively_applied_to_user_id}.`
          : '';
        setSuccess(`Added "${data.email_lower}" to whitelist.${retroNote}`);
        // Brief delay so the admin sees the result before the table refreshes
        setTimeout(onSuccess, 1200);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to add');
      } finally {
        setSubmitting(false);
      }
    },
    [apiUrl, email, reason, expiresAt, notes, onSuccess],
  );

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="font-semibold text-foreground">Add to verification whitelist</h2>
          <button onClick={onClose} className="p-1 text-foreground hover:text-muted-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="p-2 rounded bg-destructive/10 border border-destructive/30 text-destructive text-sm">
              {error}
            </div>
          )}
          {success && (
            <div className="p-2 rounded bg-green-500/10 border border-green-500/30 text-green-700 dark:text-green-300 text-sm">
              {success}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium mb-1 text-foreground">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tester@example.com"
              className="w-full px-3 py-2 text-sm border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Case-insensitive match. Survives account deletes.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1 text-foreground">Reason</label>
            <input
              type="text"
              required
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Internal tester / Investor demo / Support contact"
              className="w-full px-3 py-2 text-sm border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1 text-foreground">Expires (optional)</label>
            <input
              type="datetime-local"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              className="w-full px-3 py-2 text-sm border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Leave blank for a permanent entry.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1 text-foreground">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 text-sm border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div className="flex items-center justify-end gap-2 pt-2 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-2 text-sm font-medium text-foreground hover:bg-muted rounded-md"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !email.trim() || !reason.trim()}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Add
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
